import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowRight } from "lucide-react";
import { useEntitlements } from "@/lib/entitlements";

/**
 * Persistent banner shown to trial users. Counts down days remaining and
 * points to /pricing. Hidden once tier ≠ 'trial' or trial has expired.
 *
 * Auto-updates every minute so the "hours left" state updates without a
 * page reload as the deadline approaches.
 */
export default function TrialBanner() {
  const { entitlements } = useEntitlements();
  const tau = entitlements?.trial_active_until;

  const timeLeft = useMemo(() => {
    if (!tau) return null;
    const end = new Date(tau).getTime();
    const now = Date.now();
    const ms = end - now;
    if (ms <= 0) return null;
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (days >= 1) return { primary: `${days} day${days === 1 ? "" : "s"} left`, urgent: false };
    if (hours >= 1) return { primary: `${hours} hour${hours === 1 ? "" : "s"} left`, urgent: true };
    const mins = Math.max(1, Math.floor(ms / (1000 * 60)));
    return { primary: `${mins} minute${mins === 1 ? "" : "s"} left`, urgent: true };
  }, [tau]);

  // Re-render every 60s so the countdown stays fresh.
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  if (!entitlements) return null;
  if (entitlements.tier !== "trial") return null;
  if (!timeLeft) return null;

  return (
    <Link
      to="/pricing"
      data-testid="trial-banner"
      className={`block border-b transition-colors ${
        timeLeft.urgent
          ? "border-terracotta bg-terracotta/10 hover:bg-terracotta/20"
          : "border-obsidian-600 bg-terracotta/5 hover:bg-terracotta/10"
      }`}
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 md:px-16 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Clock
            className={`h-4 w-4 shrink-0 ${
              timeLeft.urgent ? "text-terracotta animate-pulse" : "text-terracotta"
            }`}
          />
          <p className="font-sans text-xs sm:text-sm text-bone-200 truncate">
            <span
              className="font-serif italic text-terracotta"
              data-testid="trial-banner-time"
            >
              Trial · {timeLeft.primary}
            </span>
            <span className="hidden sm:inline text-bone-400"> · after that, chapters 2–7 relock. Keep the workbook open →</span>
            <span className="sm:hidden text-bone-400"> · upgrade to keep</span>
          </p>
        </div>
        <div className="inline-flex items-center gap-1 font-sans text-[0.65rem] sm:text-xs uppercase tracking-[0.2em] text-terracotta shrink-0">
          <span>Upgrade</span>
          <ArrowRight className="h-3 w-3" />
        </div>
      </div>
    </Link>
  );
}
